import { useCallback, useEffect, useMemo, useState } from 'react';
import { ManagementPage } from '../../components/commons/ManagementPage/ManagementPage';
import { getPlaceHolder } from '../../helpers/list-helper';
import {
  Box,
  Button,
  Center,
  Checkbox,
  Flex,
  HStack,
  Input,
  Modal,
  ModalBody,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Progress,
  Select,
  SimpleGrid,
  Stack,
  Tab,
  TabList,
  Tabs,
  Text,
  Tooltip,
} from '@chakra-ui/react';
import './ScannersControlPage.scss';
import { DataTable, TableColumn } from '../../components/commons/Table/Table';
import React from 'react';
import { useDashboardApi } from '../../hooks/use-dashboard-api';
import { ScannerEnvironment } from '../../data/scanner-responses';
import { ValueFormatterParams } from '@ag-grid-community/core';
import {
  DeploymentInProgressStatuses,
  DeploymentRecord,
  ScannerResponse,
} from '../../data/scanner-responses';
import { useAtomApiKeys } from '../../services/jotai-store-service';
import { ContentSource } from '@mentaport/supplement';
import { useMentaport } from '../../hooks/use-mentaport';
import { CustomCellRendererProps } from 'ag-grid-react';

const SCANNER_INSTANCE_WAIT_TIME = 3 * 60 * 1000;

type SelectOption = {
  id: string;
  title: string;
};
type SelectData = {
  placeholder: string;
  options: SelectOption[];
};

type ScannerAction =
  | 'instanceId'
  | 'owner'
  | 'site'
  | 'deleteInstanceId'
  | 'create'
  | 'deploy'
  | 'redeploy'
  | 'checkDeploymentStatus'
  | 'fetchDeploymentsByOwner'
  | 'fetchDeploymentsBySite'
  | 'fetchDeploymentsFromInstanceId';

const ScannerActionOptions: SelectData = {
  placeholder: 'Scanner Actions',
  options: [
    {
      id: 'create',
      title: 'Deploy',
    },
    {
      id: 'site',
      title: 'Fetch Using Site Type',
    },
    {
      id: 'instanceId',
      title: 'Fetch Using Instance Id',
    },
    {
      id: 'owner',
      title: 'Fetch Using Owner Name',
    },
  ],
};

const defaultEnvironment: ScannerEnvironment = {
  EMAIL: '',
  USERNAME: '',
  PASSWORD: '',
  SCANNER_SLEEP_TIME_SECS_MIN: '',
  SCANNER_SLEEP_TIME_SECS_MAX: '',
  SITE: ContentSource.x,
  FOLLOWED_ACCOUNTS_LIST: [],
};

const defaultRedeployEnvironment: Partial<ScannerEnvironment> = {
  EMAIL: undefined,
  USERNAME: undefined,
  PASSWORD: undefined,
  SCANNER_SLEEP_TIME_SECS_MAX: undefined,
  SCANNER_SLEEP_TIME_SECS_MIN: undefined,
  FOLLOWED_ACCOUNTS_LIST: undefined,
};

const SiteOptions: SelectData = {
  placeholder: 'Site',
  options: [
    {
      id: ContentSource.x,
      title: 'X',
    },
    {
      id: ContentSource.youtube,
      title: 'YouTube',
    },
    {
      id: ContentSource.youtubeMusic,
      title: 'YouTube Music',
    },
    {
      id: ContentSource.spotify,
      title: 'Spotify',
    },
    {
      id: ContentSource.soundcloud,
      title: 'SoundCloud',
    },
    {
      id: ContentSource.tiktok,
      title: 'TikTok',
    },
  ],
};

interface CustomContractCellRendererProps extends CustomCellRendererProps {
  onGetHistory: (instanceId: string) => void;
  onRedeployInstance: (instanceId: string, site: ContentSource) => void;
  onDeleteInstance: (dinstanceId: string) => void;
  sendingRequest?: 'getHistory' | 'redeploy' | 'delete';
}

const CustomCellRenderer: React.FC<CustomContractCellRendererProps> = ({
  value,
  data,
  sendingRequest,
  onGetHistory,
  onRedeployInstance,
  onDeleteInstance,
}) => {
  const parsedData = data as { instance_id: string, site: ContentSource };
  const instanceId = parsedData.instance_id;
  const site = parsedData.site;
  const isUnavailable = !!sendingRequest;

  return (
    <HStack width={400} height={'100%'} alignItems={'center'}>
      <Button
        className={`Button Option ${isUnavailable && 'Disabled'}`}
        onClick={onGetHistory.bind(this, instanceId)}
        disabled={isUnavailable}
      >
        {sendingRequest === 'getHistory' ? 'Fetching...' : 'Fetch History'}
      </Button>
      <Button
        className={`Button Option Warning ${isUnavailable && 'Disabled'}`}
        onClick={onRedeployInstance.bind(this, instanceId, site)}
        disabled={isUnavailable}
      >
        {sendingRequest === 'redeploy' ? 'redeploying...' : 'Redeploy'}
      </Button>
      <Button
        className={`Button Option Red ${isUnavailable && 'Disabled'}`}
        onClick={onDeleteInstance.bind(this, instanceId)}
        disabled={isUnavailable}
      >
        {sendingRequest === 'delete' ? 'Deleting...' : 'Delete'}
      </Button>
    </HStack>
  );
};

export const ScannersControlPage = () => {
  const dashboardApi = useDashboardApi();
  const mentaportService = useMentaport();
  const [apiKeys, isLoading] = useAtomApiKeys();

  const [selectedAction, setSelectedAction] = useState<ScannerAction>();
  const [withHistory, setWithHistory] = useState(false);
  const [submittingAction, setSubmittingAction] = useState(false);
  const [error, setError] = useState<string>('');

  /* New Scanner Variables */
  const [newScannerOwnerName, setNewScannerOwnerName] = useState<string>('');
  const [newScannerInstanceId, setNewScannerInstanceId] = useState<string>('');
  const [newScannerEnvironment, setNewScannerEnvironment] =
    useState<ScannerEnvironment>({ ...defaultEnvironment });
  const [newScannerSource, setNewScannerSource] = useState<ContentSource>(
    ContentSource.x
  );
  const [showDeploymentStage, setShowDeploymentStage] = useState(false);
  const [
    scannerInstanceDeploymentPercentage,
    setScannerInstanceDeploymentPercentage,
  ] = useState(0);

  /* Scanner Instace Variables */
  const [scannerDataTitle, setScannerDataTitle] = useState<string>('');
  const [scannerData, setScannerData] =
    useState<(ScannerResponse)[]>();

  /* Scanner History Variables */
  const [historyDataTitle, setHistoryDataTitle] = useState<string>('');
  const [historyData, setHistoryData] = useState<DeploymentRecord[]>();


  /* Redeploy Scanner Variables */
  const [showRedeploymentVariables, setShowRedeploymentVariables] =
    useState(false);
  const [redeployScannerInstanceId, setRedeployScannerInstanceId] =
    useState<string>('');
  const [redeployScannerSiteType, setRedeployScannerSiteType] =
    useState<ContentSource>();
  const [redeployScannerEnvironment, setRedeployScannerEnvironment] = useState<
    Partial<ScannerEnvironment>
  >({ ...defaultRedeployEnvironment });

  /* Fetch by Instance Id Variables */
  const [selectedInstanceId, setSelectedInstanceId] = useState<string>('');

  /* Fetch by Owner Variables */
  const [fetchScannerOwnerName, setFetchScannerOwnerName] = useState<string>('');

  /* Fetch by Site Variables */
  const [fetchScannerSiteType, setFetchScannerSiteType] =
    useState<ContentSource>(ContentSource.x);

  /* Delete Instance Variables */
  const [deleteInstanceId, setDeleteInstanceId] = useState<string>('');
  const [deleteConfirmationModalOpen, setDeleteConfirmationModalOpen] = useState(false);

  useEffect(() => {
    const timer = setTimeout(() => {
      setError('');
    }, 4 * 1_000);

    return () => clearTimeout(timer);
  }, [error]);

  useEffect(() => {
    if (!isLoading && apiKeys.length) {
      setNewScannerEnvironment(prev => ({
        ...prev,
        VERIFY_API_KEY: apiKeys,
      }));
    }
  }, [isLoading, apiKeys]);

  useEffect(() => {
    setScannerData(undefined);
    setScannerDataTitle('');
    resetDeleteScannerFromInstanceIdVariables();
    resetFetchScannerByInstanceIdVariables();
    resetFetchAllSiteScannersVariables();
    resetFetchScannerByOwnerVariables();
    resetNewScannerVariables();
    resetNewScannerEnvironmentVariables();
    resetRedeployScannerVariables();
    resetHistoryDataVariables();
  }, [selectedAction]);

  useEffect(() => {
    if (!newScannerInstanceId) {
      setScannerInstanceDeploymentPercentage(0);
      return;
    }

    const startTime = Date.now();
    setScannerInstanceDeploymentPercentage(0);
    const interval = setInterval(() => {
      const elapsedTime = Date.now() - startTime;
      const percentage = Math.min(
        (elapsedTime / SCANNER_INSTANCE_WAIT_TIME) * 100,
        100
      );

      if (percentage >= 100) {
        clearInterval(interval);
        setScannerInstanceDeploymentPercentage(0);
      } else {
        setScannerInstanceDeploymentPercentage(percentage);
      }
    }, 10);

    return () => clearInterval(interval);
  }, [newScannerInstanceId]);


  const onRequestInstanceDeletion = useCallback((instanceId: string) => {
    setDeleteInstanceId(instanceId);
    setDeleteConfirmationModalOpen(true);
  }, [])

  const onCloseDeleteConfirmationModal = useCallback(async (canceled?: boolean) => {
    setDeleteConfirmationModalOpen(false);
    if (canceled) {
      setDeleteInstanceId('');
    } else {
      await deleteScannerFromInstanceId(deleteInstanceId)
      setDeleteInstanceId('');
      onSubmitAction();

    }
  }, [
    deleteInstanceId,
    setDeleteConfirmationModalOpen,
    setDeleteInstanceId,
  ]);

  const fetchDeploymentHistoryByInstanceId = useCallback(
    async (instanceId: string) => {
      if (dashboardApi && instanceId) {
        try {
          const res = await dashboardApi.getDeploymentsByInstance(instanceId);
          return res;
        } catch (error: any) {
          console.log('error', error);
          const message = error?.response?.data?.message;
          setError(message as string);
        }
      } else {
        setError('Please select an instance id to fetch');
      }
    },
    [dashboardApi]
  );

  const fetchUserScannerInstances = useCallback(async () => {
    if (dashboardApi) {
      try {
        return await dashboardApi.getScannerByOwner(fetchScannerOwnerName);
      } catch (error: any) {
        console.log('error', error);
        const message = error?.response?.data?.message;
        setError(message as string);
      }
    } else {
      setError('Please select an owner to fetch');
    }
  }, [dashboardApi, fetchScannerOwnerName]);

  const fetchAllSiteScanners = useCallback(
    async (scannerType?: ContentSource) => {
      const scannerTypeToUse = scannerType || fetchScannerSiteType;
      if (dashboardApi && scannerTypeToUse) {
        try {
          return await dashboardApi.getScannerBySite(scannerTypeToUse);
        } catch (error: any) {
          console.log('error', error);
          const message = error?.response?.data?.message;
          setError(message as string);
        }
      } else {
        setError('Please select a site type to fetch');
      }
    },
    [fetchScannerSiteType, dashboardApi]
  );

  const deleteScannerFromInstanceId = useCallback(
    async (instanceId: string) => {
      if (dashboardApi && instanceId) {
        try {
          return await dashboardApi.deleteInstanceByInstanceId(instanceId);
        } catch (error: any) {
          console.log('error', error);
          const message = error?.response?.data?.message;
          setError(message as string);
        }
      } else {
        setError('Please select an instance id to delete');
      }
    },
    [dashboardApi]
  );

  const fetchScannerFromInstanceId = useCallback(async () => {
    if (dashboardApi && selectedInstanceId) {
      try {
        return await dashboardApi.getScannerByInstance(selectedInstanceId);
      } catch (error: any) {
        console.log('error', error);
        const message = error?.response?.data?.message;
        setError(message as string);
      }
    } else {
      setError('Please select an instance id to fetch');
    }
  }, [selectedInstanceId, dashboardApi]);

  const deployScanner = useCallback(
    async (instanceId: string, environment: ScannerEnvironment) => {
      //if any of the environment variables are undefined, set error and return
      for (const key in environment) {
        if (
          environment[key as keyof ScannerEnvironment] === undefined ||
          environment[key as keyof ScannerEnvironment] === '' ||
          environment[key as keyof ScannerEnvironment] === null
        ) {
          setError('Please fill all environment variables');
          return;
        }
      }

      const sleepMin = parseInt(environment.SCANNER_SLEEP_TIME_SECS_MIN);
      const sleepMax = parseInt(environment.SCANNER_SLEEP_TIME_SECS_MAX);
      if (sleepMin < 0 || sleepMax < 0 || sleepMin > sleepMax) {
        setError('Invalid sleep time values');
        return;
      }

      if (
        environment.FOLLOWED_ACCOUNTS_LIST &&
        !Array.isArray(environment.FOLLOWED_ACCOUNTS_LIST)
      ) {
        environment.FOLLOWED_ACCOUNTS_LIST = (
          environment.FOLLOWED_ACCOUNTS_LIST as string
        )
          .split(',')
          .map((item: string) => item.trim());
      }

      if (dashboardApi && instanceId) {
        try {
          const res = await dashboardApi.deployScanner({
            instanceId: instanceId,
            environment_variables: environment,
          });
          if (!res.deployment_id || !res.instance_id) {
            setError(res?.message);
            return;
          }
          return res;
        } catch (error: any) {
          const message = error?.response?.data?.message;
          console.log('error', error?.response);
          setError(message as string);
        }
      }
    },
    [dashboardApi]
  );

  const redeployScanner = useCallback(
    async (
      instanceId: string,
      site: ContentSource,
      environment: Partial<ScannerEnvironment>
    ) => {
      if (dashboardApi && instanceId && site) {
        const environmentCopy = { ...environment };
        // remove any undefined values from environment
        for (const key in environmentCopy) {
          if (environmentCopy[key as keyof ScannerEnvironment] === undefined) {
            delete environmentCopy[key as keyof ScannerEnvironment];
          }

          if (
            key === 'FOLLOWED_ACCOUNTS_LIST' &&
            !!environmentCopy.FOLLOWED_ACCOUNTS_LIST &&
            !Array.isArray(environmentCopy[key as keyof ScannerEnvironment])
          ) {
            environmentCopy.FOLLOWED_ACCOUNTS_LIST = (
              environmentCopy[key as keyof ScannerEnvironment] as string
            )
              .split(',')
              .map((item: string) => item.trim());
          }
        }

        const sleepMin =
          environmentCopy.SCANNER_SLEEP_TIME_SECS_MIN !== undefined
            ? parseInt(environmentCopy.SCANNER_SLEEP_TIME_SECS_MIN)
            : null;
        const sleepMax =
          environmentCopy.SCANNER_SLEEP_TIME_SECS_MAX !== undefined
            ? parseInt(environmentCopy.SCANNER_SLEEP_TIME_SECS_MAX)
            : null;

        if (
          sleepMin !== null &&
          sleepMax !== null &&
          (sleepMin < 0 || sleepMax < 0 || sleepMin > sleepMax)
        ) {
          setError('Invalid sleep time values');
          return;
        }

        try {
          const res = await dashboardApi.redeployScanner({
            instanceId: instanceId,
            environment_variables: {
              ...environmentCopy,
              SITE: site,
            },
          });
          const data = res;
          if (data) {
            if (data.deployment_id && data.instance_id) {
              return data;
            } else {
              throw new Error(data?.data.message);
            }
          }
        } catch (error: any) {
          const message = error?.response?.data?.message;
          console.log('error', error?.response);
          setError(message as string);
        }
      } else {
        setError('Please select an instance id to redeploy');
      }
    },
    [dashboardApi]
  );

  const createNewScanner = useCallback(async () => {
    if (dashboardApi && newScannerSource && newScannerOwnerName) {
      try {
        const res = await dashboardApi.createNewScanner({
          site: newScannerSource,
          ownerName: newScannerOwnerName,
        });
        if (res.instance_id) {
          setNewScannerInstanceId(res.instance_id);
          await new Promise(resolve =>
            setTimeout(resolve, SCANNER_INSTANCE_WAIT_TIME)
          );
          setShowDeploymentStage(true);
        }
      } catch (error: any) {
        console.log('error', error);
        const message = error?.response?.data?.message;
        setError(message as string);
      }
    } else {
      setError('Please select a site and owner name to deploy');
    }
  }, [dashboardApi, newScannerSource, newScannerOwnerName]);

  const onChangeSelectedAction = useCallback(
    (event: React.ChangeEvent<HTMLSelectElement>) => {
      setSelectedAction(event.target.value as ScannerAction);
    },
    [setSelectedAction]
  );

  const onChangeFetchByInstanceIdValue = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      setSelectedInstanceId(event.target.value);
    },
    [setSelectedInstanceId]
  );

  const onChangeFetchByOwnerValue = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      // we can have a limit of 8 characters for the owner name, trim if needed
      const name = event.target.value;
      setFetchScannerOwnerName(name.length > 8 ? name.slice(0, 8) : name);
    },
    []
  );

  const onChangeFetchSiteType = useCallback(
    (event: React.ChangeEvent<HTMLSelectElement>) => {
      setFetchScannerSiteType(event.target.value as ContentSource);
    },
    []
  );

  const onChangeNewScannerSiteType = useCallback(
    (event: React.ChangeEvent<HTMLSelectElement>) => {
      const siteType = event.target.value as ContentSource;
      setNewScannerSource(siteType);
      newScannerEnvironment.SITE = siteType;
    },
    []
  );

  const onChangeNewScannerOwnerName = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const name = event.target.value;
      setNewScannerOwnerName(name.length > 8 ? name.slice(0, 8) : name);
    },
    []
  );

  const resetNewScannerVariables = useCallback(() => {
    setNewScannerSource(ContentSource.x);
    setNewScannerOwnerName('');
    setNewScannerEnvironment({} as ScannerEnvironment);
    setNewScannerInstanceId('');
    setShowDeploymentStage(false);
  }, []);

  const resetNewScannerEnvironmentVariables = useCallback(() => {
    setNewScannerEnvironment({
      ...defaultEnvironment,
    });
  }, []);

  const resetFetchScannerByInstanceIdVariables = useCallback(() => {
    setSelectedInstanceId('');
  }, []);

  const resetFetchScannerByOwnerVariables = useCallback(() => {
    setFetchScannerOwnerName('');
  }, []);

  const resetFetchAllSiteScannersVariables = useCallback(() => {
    setFetchScannerSiteType(ContentSource.x);
  }, []);

  const resetDeleteScannerFromInstanceIdVariables = useCallback(() => {
    setDeleteInstanceId('');
  }, []);

  const resetRedeployScannerVariables = useCallback(() => {
    setRedeployScannerInstanceId('');
    setRedeployScannerSiteType(undefined);
    setRedeployScannerEnvironment({ ...defaultRedeployEnvironment });
    setShowRedeploymentVariables(false);
  }, []);

  const resetHistoryDataVariables = useCallback(() => {
    setHistoryData([]);
    setHistoryDataTitle('');
  }, []);

  const onSubmitAction = useCallback(async () => {
    setSubmittingAction(true);
    let scannerData: (ScannerResponse)[] = [];
    let scannerDataTitle = '';

    switch (selectedAction) {
      case 'instanceId':
        const instanceRes = await fetchScannerFromInstanceId();
        if (instanceRes) {
          scannerData = instanceRes;
          scannerDataTitle = 'Scanner with Instance Id: ' + selectedInstanceId;
        }

        if (withHistory) {
          const historyRes = await fetchDeploymentHistoryByInstanceId(
            selectedInstanceId
          );
          if (historyRes) {
            setHistoryData(historyRes);
            setHistoryDataTitle(
              'Deployment History for ' + selectedInstanceId
            );
          }
        } else {
          setHistoryData([]);
          setHistoryDataTitle('');
        }
        break;
      case 'owner':
        const ownerRes = await fetchUserScannerInstances();
        if (ownerRes) {
          scannerData = ownerRes;
          scannerDataTitle = 'Scanners for Owner: ' + fetchScannerOwnerName;
        }

        if (withHistory && ownerRes?.length) {
          const historyPromises = ownerRes.map(scanner =>
            fetchDeploymentHistoryByInstanceId(scanner.instance_id)
          );
          const historyRes = await Promise.allSettled(historyPromises);
          if (historyRes) {
            setHistoryData(
              historyRes
                .filter((result): result is PromiseFulfilledResult<DeploymentRecord[]> => result.status === 'fulfilled')
                .flatMap(result => result.value || [])
            );
            setHistoryDataTitle('Deployment History for Owner: ' + fetchScannerOwnerName);
          }
        } else {
          setHistoryData([]);
          setHistoryDataTitle('');
        }
        break;
      case 'site':
        const siteRes = await fetchAllSiteScanners();
        if (siteRes) {
          scannerData = siteRes;
          const capitaledSite = fetchScannerSiteType
            ? fetchScannerSiteType?.charAt(0).toUpperCase() +
            fetchScannerSiteType?.slice(1)
            : '';
          scannerDataTitle = 'Scanners for Site: ' + capitaledSite;
        }

        if (withHistory && siteRes?.length) {
          const historyPromises = siteRes.map(scanner =>
            fetchDeploymentHistoryByInstanceId(scanner.instance_id)
          );
          const historyRes = await Promise.allSettled(historyPromises);
          if (historyRes) {
            setHistoryData(
              historyRes
                .filter((result): result is PromiseFulfilledResult<DeploymentRecord[]> => result.status === 'fulfilled')
                .flatMap(result => result.value || [])
            );
            setHistoryDataTitle('Deployment History for Site: ' + fetchScannerSiteType);
          }
        } else {
          setHistoryData([]);
          setHistoryDataTitle('');
        }
        break;
      case 'create':
        if (newScannerInstanceId) {
          const reployrRes = await deployScanner(newScannerInstanceId, {
            ...newScannerEnvironment,
            SITE: newScannerSource,
          });
          if (reployrRes) {
            scannerData = [reployrRes];
            scannerDataTitle =
              'Deployed Scanner with Instance Id: ' + newScannerInstanceId;
          }
        } else {
          await createNewScanner();
        }
        break;
      default:
        break;
    }

    setSubmittingAction(false);
    setScannerData(scannerData);
    setScannerDataTitle(scannerDataTitle);
  }, [
    selectedAction,
    newScannerInstanceId,
    newScannerOwnerName,
    fetchScannerSiteType,
    fetchScannerOwnerName,
    selectedInstanceId,
    newScannerEnvironment,
    redeployScannerInstanceId,
    redeployScannerSiteType,
    redeployScannerEnvironment,
    withHistory,
    deleteScannerFromInstanceId,
    fetchScannerFromInstanceId,
    fetchUserScannerInstances,
    fetchAllSiteScanners,
    createNewScanner,
    fetchDeploymentHistoryByInstanceId,
    redeployScanner,
  ]);

  const dateValueFormatter = (params: ValueFormatterParams) => {
    if (params.value) {
      const date = new Date(params.value);
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const day = String(date.getDate()).padStart(2, '0');
      const hours = String(date.getHours()).padStart(2, '0');
      const minutes = String(date.getMinutes()).padStart(2, '0');
      const seconds = String(date.getSeconds()).padStart(2, '0');

      return `${year}-${month}-${day} ${hours}:${minutes}:${seconds}`;
    }
    return null;
  };

  const getEnvironmentSelectComponent = (
    key: keyof ScannerEnvironment,
    value: any,
    selectedAction: ScannerAction = 'create'
  ) => {
    let tooltipText = '';
    const setterFunc =
      selectedAction === 'redeploy'
        ? setRedeployScannerEnvironment
        : setNewScannerEnvironment;
    const isOptional = selectedAction === 'redeploy' ? true : false;
    switch (key) {
      case 'SITE':
        return null;
      case 'SCANNER_SLEEP_TIME_SECS_MIN':
      case 'SCANNER_SLEEP_TIME_SECS_MAX':
        if (key === 'SCANNER_SLEEP_TIME_SECS_MIN') {
          tooltipText = 'Minimum time in seconds to sleep between scanner runs';
        } else {
          tooltipText = 'Maximum time in seconds to sleep between scanner runs';
        }
        if (isOptional) {
          tooltipText += ' (Optional)';
        }
        return (
          <Tooltip
            label={tooltipText}
            aria-label="A tooltip"
            placement="top"
            hasArrow
          >
            <Input
              placeholder={`${key} ${isOptional ? '(Optional)' : ''}`}
              value={value}
              disabled={submittingAction}
              accept="number"
              onChange={e => {
                const isNaN = Number.isNaN(Number(e.target.value));
                setterFunc((prev: any) => {
                  const newEnv = {
                    ...prev,
                    [key]: isNaN ? '' : e.target.value,
                  };
                  return newEnv;
                });
              }}
            />
          </Tooltip>
        );
      default:
        if (key === 'EMAIL') {
          tooltipText = 'Email used for sign-in account to use for the scanner';
        } else if (key === 'USERNAME') {
          tooltipText =
            'Username used for sign-in account to use for the scanner';
        } else if (key === 'PASSWORD') {
          tooltipText =
            'Password used for sign-in account to use for the scanner';
        } else if (key === 'FOLLOWED_ACCOUNTS_LIST') {
          tooltipText = 'List of accounts to follow. Comma separated';
        }
        if (isOptional) {
          tooltipText += ' (Optional)';
        }
        return (
          <Tooltip
            label={tooltipText}
            aria-label="A tooltip"
            placement="top"
            hasArrow
          >
            <Input
              placeholder={`${key} ${isOptional ? '(Optional)' : ''}`}
              value={value}
              disabled={submittingAction}
              onChange={e => {
                setterFunc((prev: any) => {
                  const value =
                    key === 'FOLLOWED_ACCOUNTS_LIST'
                      ? e.target.value.trim()
                      : e.target.value;
                  const newEnv = { ...prev, [key]: value };
                  return newEnv;
                });
              }}
            />
          </Tooltip>
        );
    }
  };

  const CustomInstanceActionsRenderer: React.FC<
    CustomCellRendererProps
  > = props => {
    const [sendingRequest, setSendingRequest] = useState<
      'getHistory' | 'redeploy' | 'delete'
    >();

    const onFetchHistory = useCallback(
      async (instanceId: string) => {
        if (sendingRequest) {
          return;
        }

        setSendingRequest('getHistory');
        try {
          const response = await fetchDeploymentHistoryByInstanceId(instanceId);
          if (response) {
            setHistoryData(response);
            setHistoryDataTitle(
              'Deployment History for ' + instanceId
            );
          }
        } catch (error: any) {
          console.log('error', error);
          setError(
            'Error fetching deployment history contract: ' + error?.message
          );
        } finally {
          setSendingRequest(undefined);
        }
      },
      [sendingRequest, fetchDeploymentHistoryByInstanceId]
    );

    const onRedeployInstance = useCallback(
      async (instanceId: string, site: ContentSource) => {
        if (sendingRequest) {
          return;
        }

        if (!showRedeploymentVariables) {
          setRedeployScannerInstanceId(instanceId);
          setRedeployScannerSiteType(site);
          setShowRedeploymentVariables(true);
        }
      },
      [
        sendingRequest,
        showRedeploymentVariables,
        redeployScanner,
        redeployScannerSiteType,
        redeployScannerEnvironment,
      ]
    );


    return (
      <CustomCellRenderer
        onDeleteInstance={onRequestInstanceDeletion}
        onGetHistory={onFetchHistory}
        onRedeployInstance={onRedeployInstance}
        sendingRequest={sendingRequest}
        {...props}
      />
    );
  };

  const scannerColumns: TableColumn[] = useMemo(() => {
    const tableColumns: TableColumn[] = [];
    if (scannerData?.length) {
      Object.keys(scannerData[0]).forEach(key => {
        tableColumns.push({
          // make title from snake case to space separated and capitalize first letter
          title: key.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase()),
          id: key,
          valueFormatter:
            key === 'launch_time' ? dateValueFormatter : undefined,
        });
      });

      tableColumns.push({
        title: 'Actions',
        id: 'actions',
        customRenderer: CustomInstanceActionsRenderer,
        width: 330,
      });
    }

    return tableColumns;
  }, [scannerData]);

  const historyColumns: TableColumn[] = useMemo(() => {
    const tableColumns: TableColumn[] = [];
    if (historyData?.length) {
      Object.keys(historyData[0]).forEach(key => {
        tableColumns.push({
          // make title from snake case to space separated and capitalize first letter
          title: key.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase()),
          id: key,
          valueFormatter:
            key in ['createdAt', 'updatedAt'] ? dateValueFormatter : undefined,
        });
      });
    }

    return tableColumns;
  }, [historyData]);

  const selectedActionValue = useMemo(() => {
    switch (selectedAction) {
      case 'deleteInstanceId':
        return deleteInstanceId;
      case 'instanceId':
        return selectedInstanceId;
      case 'owner':
        return fetchScannerOwnerName;
      case 'site':
        return fetchScannerSiteType;
      case 'redeploy':
        return redeployScannerInstanceId;
    }
  }, [
    selectedAction,
    deleteInstanceId,
    selectedInstanceId,
    newScannerOwnerName,
    fetchScannerSiteType,
    fetchScannerOwnerName,
  ]);

  return (
    <ManagementPage
      title={'Scanners Admin'}
      path={'scanners'}
      isLoading={false}
      previousLocation={''}
      placeholder={getPlaceHolder()}
    >
      <Stack w={'100%'} spacing={6} p={30}>
      <Modal isOpen={deleteConfirmationModalOpen} onClose={() => {}} isCentered>
          <ModalOverlay />
          <ModalContent>
            <ModalHeader>Delete Scanner Instance</ModalHeader>
            <ModalBody>
              <Text>Are you sure you want to delete this scanner instance?</Text>
            </ModalBody>

            <ModalFooter gap={2}>
              <HStack>
                <Button onClick={onCloseDeleteConfirmationModal.bind(this,true)}>Cancel</Button>
                <Button
                  colorScheme="red"
                  mr={3}
                  onClick={onCloseDeleteConfirmationModal.bind(this, false)}
                >
                  Delete
                </Button>

              </HStack>
            </ModalFooter>
          </ModalContent>
      </Modal>
        <SimpleGrid columns={1} spacing={10}>
          <Stack spacing={4}>
            {
              <Select
                className="SelectContainer"
                placeholder={ScannerActionOptions.placeholder}
                value={selectedAction}
                onChange={onChangeSelectedAction}
              >
                {ScannerActionOptions.options.map(deployOption => (
                  <option key={deployOption.id} value={deployOption.id}>
                    {deployOption.title}
                  </option>
                ))}
              </Select>
            }
            {selectedAction === 'create' && (
              <Stack spacing={4}>
                <SimpleGrid columns={2} spacing={4}>
                  <Input
                    placeholder="Owner Name"
                    value={selectedActionValue}
                    onChange={onChangeNewScannerOwnerName}
                    disabled={showDeploymentStage || submittingAction}
                  />
                  <Select
                    className="SelectContainer"
                    placeholder={SiteOptions.placeholder}
                    value={newScannerSource}
                    onChange={onChangeNewScannerSiteType}
                    disabled={showDeploymentStage || submittingAction}
                  >
                    {SiteOptions.options.map(deployOption => (
                      <option key={deployOption.id} value={deployOption.id}>
                        {deployOption.title}
                      </option>
                    ))}
                  </Select>
                  {!!newScannerInstanceId && (
                    <Input
                      placeholder="Instance Id"
                      value={newScannerInstanceId}
                      onChange={e => setNewScannerInstanceId(e.target.value)}
                      disabled
                    />
                  )}
                </SimpleGrid>
                {showDeploymentStage && (
                  <Stack spacing={4}>
                    <Text fontSize={26} fontWeight={600}>
                      Add environment variables for Scanner instance:{' '}
                      {newScannerInstanceId}
                    </Text>
                    <SimpleGrid columns={2} spacing={4}>
                      {Object.entries(newScannerEnvironment).map(
                        ([key, value]) => (
                          <div key={key}>
                            {getEnvironmentSelectComponent(
                              key as keyof ScannerEnvironment,
                              value
                            )}
                          </div>
                        )
                      )}
                    </SimpleGrid>
                    <Button
                      className="Button Warning"
                      onClick={onSubmitAction}
                      isLoading={submittingAction}
                      disabled={submittingAction}
                    >
                      Deploy Scanner
                    </Button>
                  </Stack>
                )}
                {!!scannerInstanceDeploymentPercentage && (
                  <Stack spacing={2} alignItems={'center'}>
                    <Text fontSize={18} color="orange">
                      {`Waiting for scanner instance to initalize... ${scannerInstanceDeploymentPercentage.toFixed(1)}%`}
                    </Text>
                    <Progress
                      value={scannerInstanceDeploymentPercentage}
                      size="sm"
                      colorScheme="yellow"
                      hasStripe
                      isAnimated
                      width={'100%'}
                      borderRadius={'5px'}
                    />
                  </Stack>
                )}
              </Stack>
            )}
            {selectedAction === 'instanceId' && (
              <Input
                placeholder="Instance Id"
                value={selectedActionValue}
                disabled={submittingAction}
                onChange={onChangeFetchByInstanceIdValue}
              />
            )}
            {selectedAction === 'owner' && (
              <Input
                placeholder="Owner Name"
                value={selectedActionValue}
                disabled={submittingAction}
                onChange={onChangeFetchByOwnerValue}
              />
            )}
            {selectedAction === 'site' && (
              <Select
                className="SelectContainer"
                placeholder={SiteOptions.placeholder}
                value={selectedActionValue}
                disabled={submittingAction}
                onChange={onChangeFetchSiteType}
              >
                {SiteOptions.options.map(deployOption => (
                  <option key={deployOption.id} value={deployOption.id}>
                    {deployOption.title}
                  </option>
                ))}
              </Select>
            )}
            {!!selectedAction && selectedAction !== 'create' && (
              <HStack
                style={{
                  borderRadius: '8px',
                  border: '1px solid #E2E8F0',
                  padding: '0 20px',
                  height: '40px',
                  cursor: submittingAction ? 'default' : 'pointer',
                }}
                onClick={() =>
                  !submittingAction && setWithHistory(!withHistory)
                }
                spacing={4}
              >
                <Text w={'max-content'} fontSize={'16px'}>
                  Include History?
                </Text>
                <Checkbox
                  size={'lg'}
                  colorScheme="green"
                  isChecked={withHistory}
                  disabled={submittingAction}
                />
              </HStack>
            )}
            <Flex w={'100%'} h={'46px'}>
              <HStack w={'100%'} spacing={4}>
                {!showDeploymentStage && (
                  <Button
                    className="Button Warning"
                    w="100%"
                    onClick={onSubmitAction}
                    isLoading={submittingAction}
                    disabled={submittingAction}
                  >
                    {`${submittingAction ? 'Processing...' : 'Submit'}`}
                  </Button>
                )}
              </HStack>
            </Flex>
          </Stack>
        </SimpleGrid>
        <Center>
          <Text color="red">{error}</Text>
        </Center>
        <SimpleGrid columns={showRedeploymentVariables || historyData?.length ? 2 : 1} spacing={10}>
          <DataTable
            title={scannerDataTitle}
            columns={scannerColumns}
            data={scannerData || []}
            rowHeight={60}
            getRowStyle={(params: any) => {
              const status = String(
                params.data?.status ||
                params.data?.state?.toLowerCase() ||
                params.data?.message?.toLowerCase()
              );
              // pending statuses DeploymentInProgressStatuses are subsets of status
              let statusIsPending = false;
              DeploymentInProgressStatuses.forEach(pendingStatus => {
                if (status?.includes(pendingStatus)) {
                  statusIsPending = true;
                }
              });
              if (status === 'running' || status === 'completed') {
                return {
                  backgroundColor: 'rgba(193, 255, 222, 0.6)',
                  height: '50px',
                };
              } else if (statusIsPending) {
                return {
                  backgroundColor: 'rgba(255, 222, 168, 0.6)',
                };
              } else {
                return {
                  backgroundColor: 'rgba(255, 193, 193, 0.6)',
                };
              }
            }}
          />
          {historyData?.length && (
            <DataTable
              title={historyDataTitle}
              columns={historyColumns}
              data={historyData || []}
              rowHeight={60}
              getRowStyle={(params: any) => {
                const status = String(
                  params.data?.status ||
                  params.data?.state?.toLowerCase() ||
                  params.data?.message?.toLowerCase()
                );
                // pending statuses DeploymentInProgressStatuses are subsets of status
                let statusIsPending = false;
                DeploymentInProgressStatuses.forEach(pendingStatus => {
                  if (status?.includes(pendingStatus)) {
                    statusIsPending = true;
                  }
                });
                if (status === 'running' || status === 'completed') {
                  return {
                    backgroundColor: 'rgba(193, 255, 222, 0.6)',
                    height: '50px',
                  };
                } else if (statusIsPending) {
                  return {
                    backgroundColor: 'rgba(255, 222, 168, 0.6)',
                  };
                } else {
                  return {
                    backgroundColor: 'rgba(255, 193, 193, 0.6)',
                  };
                }
              }}
            />
          )}
          {showRedeploymentVariables && (
            <Stack spacing={4}>
              <Text fontSize={26} fontWeight={600}>
                Add Any New Redeployment Variables for Scanner instance:{' '}
                {redeployScannerInstanceId}
              </Text>
              <SimpleGrid columns={1} spacing={4}>
                <Select
                  className="SelectContainer"
                  placeholder={SiteOptions.placeholder}
                  value={redeployScannerSiteType}
                  disabled={submittingAction}
                  onChange={e =>
                    setRedeployScannerSiteType(
                      e.target.value as ContentSource
                    )
                  }
                >
                  {SiteOptions.options.map(deployOption => (
                    <option key={deployOption.id} value={deployOption.id}>
                      {deployOption.title}
                    </option>
                  ))}
                </Select>
              </SimpleGrid>
              <SimpleGrid columns={2} spacing={4}>
                {Object.entries(redeployScannerEnvironment).map(
                  ([key, value]) => (
                    <div key={key}>
                      {getEnvironmentSelectComponent(
                        key as keyof ScannerEnvironment,
                        value,
                        selectedAction
                      )}
                    </div>
                  )
                )}
              </SimpleGrid>
              <Button
                className="Button Warning"
                onClick={redeployScanner.bind(this, redeployScannerInstanceId, redeployScannerSiteType!, redeployScannerEnvironment)}
                isLoading={submittingAction}
                disabled={submittingAction}
              >
                Redeploy Scanner
              </Button>
            </Stack>
          )}
        </SimpleGrid>
      </Stack>
    </ManagementPage>
  );
};
